/**
 * Cambrian drawing primitives.
 *
 * Parallels `src/visuals/bio.ts` but ships its helpers locally so the
 * Cambrian creature/player look has organic, segmented anatomy on par
 * with Phase 1's eukaryote primitives — no flat ellipses.
 *
 * What lives here:
 *   - low-level helpers (softHalo, wobblyBlob, wavyLine, mix)
 *   - the player composite (drawPlayerComposite) — a Haikouichthys-like
 *     fish; grown body parts (stinger, mandibles, fins, quills, …) attach
 *     at fixed anatomical spots and grow with each upgrade level.
 *   - per-tier creature draws using real Cambrian anatomy:
 *       drawPlanktonOrganism — nucleus + cytoplasm halo
 *       drawTrilobite        — cephalon + thorax + pygidium with eyes
 *       drawHuntingWorm      — segmented annelid with parapodia
 *       drawAnomalocaris     — apex with raptorial appendages + lateral
 *                              swimming lobes + stalked eyes + tail fan
 *
 * All draws assume +x is forward in body-local space; the caller rotates
 * the Graphics container globally by heading.
 */

import { Graphics } from 'pixi.js';
import type { PartId, PartLevels } from './parts';

// ---- low-level helpers ---------------------------------------------

export function softHalo(g: Graphics, radius: number, color: number, layers = 3, alpha = 0.10): void {
  for (let i = 0; i < layers; i++) {
    const a = alpha - i * (alpha / layers);
    if (a <= 0) continue;
    g.circle(0, 0, radius * (1 + i * 0.25)).fill({ color, alpha: a });
  }
}

export function mix(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const gg = Math.round(ag + (bg - ag) * t);
  const bb2 = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (gg << 8) | bb2;
}

/** Closed wobbly blob outline (call .fill / .stroke after). */
export function wobblyBlob(g: Graphics, radius: number, points: number, wobble: number, time: number, seed = 0): void {
  const pts: number[] = [];
  for (let i = 0; i < points; i++) {
    const a = (i / points) * Math.PI * 2;
    const n = Math.sin(a * 3 + time + seed) * 0.5 + Math.cos(a * 5 + time * 0.7 + seed * 1.3) * 0.5;
    const r = radius * (1 + n * wobble);
    pts.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.poly(pts);
}

/** Wavy line (flagella, tentacles, kelp fronds). */
export function wavyLine(
  g: Graphics, x1: number, y1: number, x2: number, y2: number,
  amp: number, waves: number, time: number,
  color: number, alpha: number, width: number, phase = 0,
): void {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  const steps = 14;
  let started = false;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const w = Math.sin(t * waves * Math.PI * 2 + time + phase) * amp * t;
    const px = x1 + dx * t + nx * w;
    const py = y1 + dy * t + ny * w;
    if (!started) { g.moveTo(px, py); started = true; }
    else g.lineTo(px, py);
  }
  g.stroke({ color, alpha, width });
}

// ---- PLAYER composite ----------------------------------------------

/** Transient part animation state fed in by the stage each frame. */
export interface PartFx {
  /** Quill regrowth after a volley, 0 (just fired) → 1 (loaded). */
  quillLoad: number;
  /** Shield hits currently banked. */
  shieldCharges: number;
  /** 1 → 0 flashes after a sting / a shield block / a thorn hit. */
  stingFlash: number;
  shieldFlash: number;
  thornFlash: number;
  /** 1 → 0 over a lunge (mandibles snap shut). */
  lunge: number;
}

export const NO_PART_FX: PartFx = { quillLoad: 1, shieldCharges: 0, stingFlash: 0, shieldFlash: 0, thornFlash: 0, lunge: 0 };

export interface PlayerDrawCtx {
  /** Body radius. Player anatomy scales linearly off this. */
  r: number;
  /** Shared time clock for animation. */
  t: number;
  /** Magnitude of player velocity (px/s). Drives tail flutter speed. */
  speed: number;
  /** Grown parts and their level. */
  parts: PartLevels;
  fx?: PartFx;
}

/** Drawn into the halo Graphics — behind body. */
export function drawPlayerHalo(g: Graphics, ctx: PlayerDrawCtx): void {
  g.clear();
  const { r, t } = ctx;
  const breathe = 1.0 + Math.sin(t * 2.0) * 0.05;
  const pulse = 0.55 + 0.35 * Math.sin(t * 1.7);
  g.circle(0, 0, r * 2.6 * breathe).fill({ color: 0xffd070, alpha: 0.06 + 0.04 * pulse });
  g.circle(0, 0, r * 1.9 * breathe).fill({ color: 0xffe9aa, alpha: 0.09 + 0.06 * pulse });
}

type Body = ReturnType<typeof haikouBody>;

/**
 * The player fish, drawn in body-local space with +x = forward, seen from
 * above. Modelled on Haikouichthys: lanceolate body, W-shaped myomeres,
 * gill pouches, paired eyes, fin fold into the tail. Grown parts attach at
 * fixed anatomical spots and get visibly bigger / fancier per level.
 */
export function drawPlayerComposite(g: Graphics, ctx: PlayerDrawCtx): void {
  g.clear();
  const { r, t, speed, parts } = ctx;
  const fx = ctx.fx ?? NO_PART_FX;
  const L = (id: PartId): number => parts[id] ?? 0;

  const baseColor = 0xf9c876;
  const outlineColor = 0x5a2e08;
  const bellyColor = mix(0xffe1a0, 0xfff6c0, 0.5);

  const breathe = 1.0 + Math.sin(t * 2.0) * 0.04;
  const fastness = Math.min(1, speed / 240);
  const flutter = Math.sin(t * (9 + fastness * 9)) * (0.20 + fastness * 0.5);

  const bodyLen = r * 2.4 * breathe;
  const bodyHei = r * 1.05 * (2 - breathe);
  const body = haikouBody(r, bodyLen, bodyHei, t, fastness);

  // --- under the body: things that stick out from the edges ---
  if (L('fins')) drawFins(g, r, body, L('fins'), t, fastness, outlineColor);
  if (L('gills')) drawGillPlumes(g, r, body, L('gills'), t);
  if (L('thorns')) drawThorns(g, r, body, L('thorns'), fx.thornFlash, outlineColor);
  drawCaudalFin(g, r, flutter, outlineColor, baseColor, L('tail'), t);

  // Fin fold: a translucent membrane along the rear two-thirds of the midline.
  {
    const pts: number[] = [];
    for (let i = 0; i <= 10; i++) {
      const u = 0.02 + (i / 10) * 0.62;
      const p = body.at(u);
      pts.push(p.x, p.y - body.hw(u) * 0.35 - r * 0.14 * (1 - u));
    }
    for (let i = 10; i >= 0; i--) {
      const u = 0.02 + (i / 10) * 0.62;
      const p = body.at(u);
      pts.push(p.x, p.y + body.hw(u) * 0.35 + r * 0.14 * (1 - u));
    }
    g.poly(pts).fill({ color: mix(baseColor, 0xffffff, 0.35), alpha: 0.28 });
  }
  // Body silhouette + soft edge.
  g.poly(body.outline).fill({ color: outlineColor, alpha: 0.35 });
  g.poly(body.outline).fill({ color: baseColor, alpha: 0.95 });
  // Belly: lighter band catching the light.
  {
    const pts: number[] = [];
    for (let i = 0; i <= 12; i++) { const u = 0.05 + (i / 12) * 0.85; const p = body.at(u); pts.push(p.x, p.y + body.hw(u) * 0.15); }
    for (let i = 12; i >= 0; i--) { const u = 0.05 + (i / 12) * 0.85; const p = body.at(u); pts.push(p.x, p.y + body.hw(u) * 0.75); }
    g.poly(pts).fill({ color: bellyColor, alpha: 0.35 });
  }
  // Myomeres: nine W-shaped muscle blocks, chevrons pointing forward.
  const myo = mix(outlineColor, baseColor, 0.35);
  for (let i = 0; i < 9; i++) {
    const u = 0.10 + i * 0.062;
    const p = body.at(u);
    const hw = body.hw(u);
    const back = r * 0.11;
    g.moveTo(p.x, p.y - hw * 0.88)
      .lineTo(p.x + back, p.y - hw * 0.45)
      .lineTo(p.x, p.y)
      .lineTo(p.x + back, p.y + hw * 0.45)
      .lineTo(p.x, p.y + hw * 0.88)
      .stroke({ color: myo, width: 0.9, alpha: 0.55 });
  }
  // Notochord / dorsal line along the midline.
  {
    let started = false;
    for (let i = 0; i <= 16; i++) {
      const u = 0.02 + (i / 16) * 0.84;
      const p = body.at(u);
      if (!started) { g.moveTo(p.x, p.y); started = true; } else g.lineTo(p.x, p.y);
    }
    g.stroke({ color: mix(baseColor, 0x000000, 0.35), width: 1.1, alpha: 0.45 });
  }

  // --- on the back ---
  if (L('quills')) drawQuills(g, r, body, L('quills'), fx.quillLoad, t, outlineColor);

  // Head cap: slightly paler.
  const headU = 0.78;
  {
    const pts: number[] = [];
    for (let i = 0; i <= 8; i++) { const u = headU + (i / 8) * (1 - headU); const p = body.at(u); pts.push(p.x, p.y - body.hw(u)); }
    pts.push(...body.noseArc());
    for (let i = 8; i >= 0; i--) { const u = headU + (i / 8) * (1 - headU); const p = body.at(u); pts.push(p.x, p.y + body.hw(u)); }
    g.poly(pts).fill({ color: mix(baseColor, 0xffffff, 0.16), alpha: 0.9 });
  }
  if (L('shield')) drawHeadPlate(g, r, body, L('shield'), outlineColor);
  // Mouth: a short line across the blunt snout.
  const tip = body.at(1);
  const tipX = tip.x + body.hw(1);
  g.moveTo(tipX - r * 0.06, tip.y - r * 0.10).lineTo(tipX, tip.y).lineTo(tipX - r * 0.06, tip.y + r * 0.10)
    .stroke({ color: outlineColor, width: 1.0, alpha: 0.7 });
  // Gill pouches: six per side, just behind the head (red when gills grown).
  const gill = L('gills') ? mix(0xd02030, baseColor, 0.2) : mix(baseColor, 0x000000, 0.45);
  for (let i = 0; i < 6; i++) {
    const u = 0.60 + i * 0.033;
    const p = body.at(u);
    const hw = body.hw(u);
    for (const s of [-1, 1]) {
      g.ellipse(p.x, p.y + s * hw * 0.62, r * 0.045, r * 0.11).fill({ color: gill, alpha: L('gills') ? 0.6 : 0.38 });
    }
  }
  if (L('jaws')) drawMandibles(g, r, body, L('jaws'), t, fx.lunge, outlineColor);
  g.poly(body.outline).stroke({ color: outlineColor, width: 1.5, alpha: 0.85 });
  // Paired eyes on the head, looking forward.
  const eu = 0.90;
  const ep = body.at(eu);
  const ehw = body.hw(eu);
  for (const s of [-1, 1]) {
    const ex = ep.x, ey = ep.y + s * ehw * 0.48;
    g.circle(ex, ey, r * 0.16).fill({ color: 0x1a0608, alpha: 0.95 });
    g.circle(ex + r * 0.02, ey, r * 0.11).fill({ color: 0xff6020, alpha: 0.9 });
    g.circle(ex + r * 0.06, ey - s * r * 0.03, r * 0.045).fill({ color: 0xfff3d2 });
  }
  // Stinger grows from the forehead, right between the eyes.
  if (L('stinger')) drawStinger(g, r, body, L('stinger'), t, fx.stingFlash, outlineColor);
  // Shield bubble on top of everything.
  if (L('shield')) drawShieldBubble(g, r, L('shield'), fx.shieldCharges, fx.shieldFlash, t);
}

// --- parts ------------------------------------------------------------

function drawStinger(g: Graphics, r: number, body: Body, lv: number, t: number, flash: number, outline: number): void {
  const base = body.at(0.9);           // same station as the eyes, on the midline
  const noseX = body.at(1).x + body.hw(1);
  const tipX = noseX + r * (0.45 + lv * 0.22);
  const y = base.y;
  const w = r * (0.07 + lv * 0.02);
  const col = mix(0xff5a78, 0xffffff, flash * 0.5);
  // Venom sac at the base (L3), throbbing.
  if (lv >= 3) {
    const throb = 1 + Math.sin(t * 5) * 0.1;
    g.circle(base.x - r * 0.05, y, r * 0.17 * throb).fill({ color: 0xc040ff, alpha: 0.35 });
    g.circle(base.x - r * 0.05, y, r * 0.11 * throb).fill({ color: 0xe070ff, alpha: 0.85 });
  }
  // Shaft: a tapered spike.
  g.poly([base.x, y - w, tipX, y, base.x, y + w]).fill({ color: col, alpha: 0.97 });
  g.poly([base.x, y - w, tipX, y, base.x, y + w]).stroke({ color: outline, width: 1, alpha: 0.8 });
  // Ridge highlight.
  g.moveTo(base.x, y - w * 0.3).lineTo(tipX - r * 0.1, y).stroke({ color: 0xffffff, width: 0.8, alpha: 0.5 });
  // Barbs (L2+): hooks angled back along the shaft.
  if (lv >= 2) {
    const n = lv >= 3 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const f = 0.45 + i * 0.16;
      const bx = base.x + (tipX - base.x) * f;
      const bw = w * (1 - f) + r * 0.02;
      for (const s of [-1, 1]) {
        g.poly([bx, y + s * bw, bx - r * 0.14, y + s * (bw + r * 0.1), bx - r * 0.05, y + s * bw * 0.5])
          .fill({ color: mix(col, 0x000000, 0.2), alpha: 0.95 });
      }
    }
  }
  // Glowing tip + a venom bead.
  const glow = (lv >= 3 ? 0.5 : 0.25) + flash * 0.5 + Math.sin(t * 4) * 0.08;
  g.circle(tipX, y, r * (0.1 + flash * 0.12)).fill({ color: lv >= 3 ? 0xe070ff : 0xffb0c0, alpha: glow });
  if (lv >= 3) {
    const drip = (t * 0.8) % 1;
    g.circle(tipX - r * 0.15 - drip * r * 0.3, y + r * 0.05 + drip * r * 0.25, r * 0.045 * (1 - drip))
      .fill({ color: 0xe070ff, alpha: 0.8 * (1 - drip) });
  }
}

function drawMandibles(g: Graphics, r: number, body: Body, lv: number, t: number, lunge: number, outline: number): void {
  const c = body.at(0.96);
  const hw = body.hw(0.96);
  const len = r * (0.32 + lv * 0.1);
  const wd = r * (0.06 + lv * 0.015);
  // Idle chew, snapped shut on a lunge.
  const open = (0.5 + Math.sin(t * 3) * 0.12) * (1 - lunge);
  const col = lv >= 3 ? 0xffd070 : 0xe0a860;
  for (const s of [-1, 1]) {
    const bx = c.x, by = c.y + s * hw * 0.8;
    // Centre line of the jaw: out forward, then hooking in toward the midline.
    // Centre line of the jaw: forward and splayed out, hooking in at the tip.
    const pt = (f: number): { x: number; y: number } => ({
      x: bx + Math.sin(f * Math.PI * 0.5) * len,
      y: by + s * len * 0.45 * open * Math.sin(f * Math.PI) - s * len * 0.38 * f * f,
    });
    const N = 8;
    const left: number[] = [], right: number[] = [];
    for (let i = 0; i <= N; i++) {
      const f = i / N;
      const p = pt(f);
      const q = pt(Math.min(1, f + 0.01));
      const dx = q.x - p.x || 1e-3, dy = q.y - p.y;
      const d = Math.hypot(dx, dy);
      const nx = -dy / d, ny = dx / d;
      const w = wd * (1 - f * 0.9);
      left.push(p.x + nx * w, p.y + ny * w);
      right.unshift(p.x - nx * w, p.y - ny * w);
    }
    // right was built in reverse pairs — fix pair order.
    const back: number[] = [];
    for (let i = 0; i < right.length; i += 2) back.push(right[i], right[i + 1]);
    const poly = [...left, ...back];
    g.poly(poly).fill({ color: col, alpha: 0.97 });
    g.poly(poly).stroke({ color: outline, width: 1, alpha: 0.85 });
    // Teeth on the inner edge (L2+).
    if (lv >= 2) {
      for (let i = 1; i <= lv; i++) {
        const f = 0.25 + (i / (lv + 1)) * 0.5;
        const p = pt(f);
        g.poly([p.x - r * 0.035, p.y - s * wd * 0.5, p.x + r * 0.035, p.y - s * wd * 0.5, p.x, p.y - s * (wd + r * 0.08)])
          .fill({ color: 0xfff6e0, alpha: 0.95 });
      }
    }
    const tip = pt(1);
    g.circle(tip.x, tip.y, r * 0.035).fill({ color: 0xffffff, alpha: 0.85 });
  }
}

function drawFins(g: Graphics, r: number, body: Body, lv: number, t: number, fastness: number, outline: number): void {
  const pairs: Array<{ u: number; size: number }> = [{ u: 0.52, size: 0.7 + lv * 0.18 }];
  if (lv >= 3) pairs.push({ u: 0.3, size: 0.6 });
  const rays = 3 + lv;
  for (const { u, size } of pairs) {
    const p = body.at(u);
    const hw = body.hw(u);
    const beat = Math.sin(t * (5 + fastness * 8) + u * 3) * 0.15;
    for (const s of [-1, 1]) {
      const rootF = { x: p.x + r * 0.12, y: p.y + s * hw * 0.7 };
      const rootB = { x: p.x - r * 0.28, y: p.y + s * hw * 0.7 };
      // Swept-back wing: leading edge bows out, tip trails behind the root.
      const tip = { x: rootF.x - r * size * 0.55, y: rootF.y + s * r * size * (0.9 + beat) };
      const tipF = tip;
      const tipB = { x: tip.x - r * size * 0.25, y: tip.y - s * r * size * 0.12 };
      g.moveTo(rootF.x, rootF.y)
        .quadraticCurveTo(rootF.x + r * size * 0.05, rootF.y + s * r * size * 0.75, tipF.x, tipF.y)
        .lineTo(tipB.x, tipB.y)
        .quadraticCurveTo(rootB.x - r * size * 0.2, rootB.y + s * r * size * 0.35, rootB.x, rootB.y)
        .closePath().fill({ color: 0x60d8ff, alpha: 0.5 });
      g.moveTo(rootF.x, rootF.y)
        .quadraticCurveTo(rootF.x + r * size * 0.05, rootF.y + s * r * size * 0.75, tipF.x, tipF.y)
        .stroke({ color: 0x2a88b0, width: 1.4, alpha: 0.9 });
      for (let i = 0; i < rays; i++) {
        const f = (i + 0.5) / rays;
        const ex = tipF.x + (tipB.x - tipF.x) * f, ey = tipF.y + (tipB.y - tipF.y) * f;
        const rx = rootF.x + (rootB.x - rootF.x) * f, ry = rootF.y + (rootB.y - rootF.y) * f;
        g.moveTo(rx, ry).lineTo(ex, ey).stroke({ color: 0x2a88b0, width: 0.8, alpha: 0.55 });
      }
    }
  }
}

function drawThorns(g: Graphics, r: number, body: Body, lv: number, flash: number, outline: number): void {
  const stations = [0.48, 0.22, 0.36, 0.1].slice(0, lv + 1);
  const len = r * (0.28 + lv * 0.09) * (1 + flash * 0.2);
  const col = mix(0xff8a40, 0xffffff, flash * 0.5);
  for (const u of stations) {
    const p = body.at(u);
    const hw = body.hw(u);
    for (const s of [-1, 1]) {
      const by = p.y + s * hw * 0.8;
      const tx = p.x - len * 0.55, ty = by + s * len;
      g.poly([p.x + r * 0.09, by, tx, ty, p.x - r * 0.09, by]).fill({ color: col, alpha: 0.97 });
      g.poly([p.x + r * 0.09, by, tx, ty, p.x - r * 0.09, by]).stroke({ color: outline, width: 0.9, alpha: 0.8 });
      if (lv >= 3) g.circle(tx, ty, r * 0.05).fill({ color: 0xff3020, alpha: 0.9 });
    }
  }
}

function drawQuills(g: Graphics, r: number, body: Body, lv: number, load: number, t: number, outline: number): void {
  const n = 1 + lv * 2;
  const grow = 0.25 + 0.75 * load;
  const ready = load >= 1;
  for (let i = 0; i < n; i++) {
    const u = 0.62 - i * (0.4 / Math.max(1, n - 1));
    const p = body.at(u);
    const side = i % 2 === 0 ? -1 : 1;
    const len = r * (0.55 + lv * 0.08) * grow;
    const bx = p.x + r * 0.08, by = p.y + side * r * 0.08;
    const tx = bx - len, ty = by + side * len * 0.35;
    g.moveTo(bx, by).lineTo(tx, ty).stroke({ color: 0x2a1040, width: 2.8, alpha: 0.9 });
    g.moveTo(bx, by).lineTo(tx, ty).stroke({ color: 0x9a60e0, width: 1.4, alpha: 1 });
    const shine = ready ? 0.75 + Math.sin(t * 6 + i) * 0.25 : 0.3;
    g.circle(bx, by, r * 0.06).fill({ color: 0x8050c0, alpha: 0.9 });
    g.circle(bx + (tx - bx) * 0.05, by, r * 0.03).fill({ color: 0xffffff, alpha: shine * 0.6 });
    g.circle(tx, ty, r * 0.035).fill({ color: 0xffffff, alpha: shine });
  }
}

function drawGillPlumes(g: Graphics, r: number, body: Body, lv: number, t: number): void {
  const n = lv + 1;
  for (let i = 0; i < n; i++) {
    const u = 0.72 - i * 0.045;
    const p = body.at(u);
    const hw = body.hw(u);
    for (const s of [-1, 1]) {
      const bx = p.x, by = p.y + s * hw * 0.8;
      const len = r * (0.4 + lv * 0.08);
      const sway = Math.sin(t * 3 + i + (s > 0 ? 1 : 0)) * r * 0.08;
      const tx = bx - len * 0.7 + sway, ty = by + s * len * 0.75;
      g.moveTo(bx, by).quadraticCurveTo(bx - len * 0.1, by + s * len * 0.5, tx, ty)
        .stroke({ color: 0xd02030, width: 2.2, alpha: 0.85 });
      // Feathery filaments.
      for (let k = 1; k <= 3; k++) {
        const f = k / 4;
        const fx2 = bx + (tx - bx) * f, fy = by + (ty - by) * f;
        g.moveTo(fx2, fy).lineTo(fx2 - r * 0.1, fy + s * r * 0.02).stroke({ color: 0xff7070, width: 1, alpha: 0.75 });
      }
      g.circle(tx, ty, r * 0.05).fill({ color: 0xff8080, alpha: 0.9 });
    }
  }
}

function drawHeadPlate(g: Graphics, r: number, body: Body, lv: number, outline: number): void {
  // Armoured cephalic plate over the crown, behind the eyes; grows back
  // over the neck with each level.
  const u0 = 0.8 - lv * 0.05, u1 = 0.88;
  const pts: number[] = [];
  for (let i = 0; i <= 6; i++) { const u = u0 + (i / 6) * (u1 - u0); const p = body.at(u); pts.push(p.x, p.y - body.hw(u) * 0.72); }
  for (let i = 6; i >= 0; i--) { const u = u0 + (i / 6) * (u1 - u0); const p = body.at(u); pts.push(p.x, p.y + body.hw(u) * 0.72); }
  g.poly(pts).fill({ color: 0x6f90d8, alpha: 0.9 });
  g.poly(pts).stroke({ color: mix(0x6f90d8, outline, 0.6), width: 1.1, alpha: 0.9 });
  // Plate seams + gloss.
  for (let i = 1; i < lv + 1; i++) {
    const u = u0 + (i / (lv + 1)) * (u1 - u0);
    const p = body.at(u);
    const hw = body.hw(u) * 0.7;
    g.moveTo(p.x, p.y - hw).lineTo(p.x, p.y + hw).stroke({ color: 0x324a80, width: 0.9, alpha: 0.7 });
  }
  const m = body.at((u0 + u1) / 2);
  g.ellipse(m.x + r * 0.03, m.y - body.hw(u1) * 0.3, r * 0.12, r * 0.05).fill({ color: 0xffffff, alpha: 0.35 });
}

function drawShieldBubble(g: Graphics, r: number, lv: number, charges: number, flash: number, t: number): void {
  if (flash > 0) {
    // Shattering ring on a block.
    const rr = r * (2 + (1 - flash) * 1.2);
    g.circle(0, 0, rr).stroke({ color: 0xbfe0ff, width: 3 * flash + 0.5, alpha: flash });
  }
  if (charges <= 0) return;
  const rr = r * 1.95;
  const shimmer = 0.5 + 0.5 * Math.sin(t * 2.2);
  g.circle(0, 0, rr).fill({ color: 0x80b0ff, alpha: 0.05 + shimmer * 0.03 });
  g.circle(0, 0, rr).stroke({ color: 0xa8d0ff, width: 1.4, alpha: 0.35 + shimmer * 0.2 });
  // Hex facets sliding round the bubble.
  const facets = 6 + lv * 2;
  for (let i = 0; i < facets; i++) {
    const a = (i / facets) * Math.PI * 2 + t * 0.3;
    const a2 = a + Math.PI / facets;
    g.moveTo(Math.cos(a) * rr, Math.sin(a) * rr).lineTo(Math.cos(a2) * rr * 0.93, Math.sin(a2) * rr * 0.93)
      .stroke({ color: 0xd8ecff, width: 1, alpha: 0.25 + shimmer * 0.15 });
  }
  if (charges >= 2) g.circle(0, 0, rr * 1.08).stroke({ color: 0xa8d0ff, width: 1, alpha: 0.3 });
}

function drawCaudalFin(g: Graphics, r: number, flutter: number, outline: number, baseColor: number, lv: number, t: number): void {
  const baseX = -r * 1.05;
  const size = 1 + lv * 0.2;
  const tipX = baseX - r * 0.9 * size;
  const spread = (r * 0.7 + Math.abs(flutter) * r * 0.25) * size;
  const sweep = flutter * r * 0.3 * size;
  const fill = lv ? mix(baseColor, 0x70f0c0, 0.25 + lv * 0.15) : baseColor;
  const lobes: Array<[number, number]> = [[-1, 1], [1, 1]];
  // Power tail L2+: a third, central lobe.
  if (lv >= 2) lobes.push([0, 0.8]);
  for (const [s, k] of lobes) {
    const tx = tipX + (s === 0 ? -r * 0.1 : 0), ty = s * spread * k + sweep;
    const ix = tipX + r * 0.25, iy = s * spread * 0.4 * k + sweep;
    g.poly([baseX, 0, tx, ty, ix, iy]).fill({ color: fill, alpha: 0.85 });
    g.poly([baseX, 0, tx, ty, ix, iy]).stroke({ color: outline, width: 1.4, alpha: 0.8 });
    // L3: glowing tips trailing light.
    if (lv >= 3) g.circle(tx, ty, r * (0.08 + Math.sin(t * 8 + s) * 0.02)).fill({ color: 0xb0fff0, alpha: 0.75 });
  }
  // Rays diverging from the base.
  const rays = 3 + lv * 2;
  for (let i = 0; i < rays; i++) {
    const f = rays === 1 ? 0 : (i / (rays - 1)) * 2 - 1;
    g.moveTo(baseX, 0).lineTo(tipX + Math.abs(f) * r * 0.15, f * spread * 0.6 + sweep * 0.7)
      .stroke({ color: lv ? 0xd8fff4 : outline, width: 0.8, alpha: lv ? 0.5 : 0.55 });
  }
}

/**
 * Parametric body for the player: u=0 at the caudal peduncle (tail base),
 * u=1 at the snout. Half-width tapers to a narrow peduncle, is widest
 * two-thirds forward, and rounds off into the head. A slow lateral wave
 * scaled by speed makes the body undulate like a swimming fish.
 */
function haikouBody(r: number, bodyLen: number, bodyHei: number, t: number, fastness: number): {
  at: (u: number) => { x: number; y: number };
  hw: (u: number) => number;
  /** Semicircle closing the blunt snout, top → bottom, as a flat point list. */
  noseArc: () => number[];
  outline: number[];
} {
  const half = bodyLen * 0.5;
  const tailX = -r * 1.05; // matches drawCaudalFin's base
  // Snout anchor sits well inside the old pointed tip; the rounded nose
  // (radius = front half-width) then closes it off close to the body.
  const noseX = half * 0.8;
  const wave = r * (0.03 + fastness * 0.06);
  const at = (u: number): { x: number; y: number } => ({
    x: tailX + (noseX - tailX) * u,
    y: Math.sin(u * Math.PI * 1.6 - t * (4 + fastness * 6)) * wave * (1 - u) * (1 - u),
  });
  const hw = (u: number): number => {
    const maxHw = bodyHei * 0.5;
    if (u < 0.62) {
      const s = u / 0.62;
      const smooth = s * s * (3 - 2 * s);
      return maxHw * (0.20 + 0.80 * Math.pow(smooth, 0.9));
    }
    // Head: elliptical taper that stops short of a point (≈44% width at
    // the front) so the snout can be rounded off instead of pointed.
    const s = (u - 0.62) / 0.38;
    return maxHw * Math.sqrt(Math.max(0, 1 - (s * 0.9) * (s * 0.9)));
  };
  const noseArc = (): number[] => {
    const c = at(1);
    const nr = hw(1);
    const pts: number[] = [];
    for (let i = 1; i < 8; i++) {
      const a = -Math.PI / 2 + (i / 8) * Math.PI;
      pts.push(c.x + Math.cos(a) * nr, c.y + Math.sin(a) * nr);
    }
    return pts;
  };
  const outline: number[] = [];
  const N = 22;
  for (let i = 0; i <= N; i++) { const u = i / N; const p = at(u); outline.push(p.x, p.y - hw(u)); }
  outline.push(...noseArc());
  for (let i = N; i >= 0; i--) { const u = i / N; const p = at(u); outline.push(p.x, p.y + hw(u)); }
  return { at, hw, noseArc, outline };
}

// ============================================================================
// CREATURE draws — real Cambrian anatomy
// ============================================================================

export interface CreatureDrawCtx {
  r: number;        // size from def
  t: number;        // time
  phase: number;    // per-creature random phase
  heading: number;  // world heading in radians (+x = forward in body-local)
  color: number;    // base body color
}

export function drawPlanktonOrganism(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, color } = ctx;
  const breathe = 1.0 + Math.sin(t * 1.8 + phase) * 0.10;
  // Soft halo
  softHalo(g, r * 1.8 * breathe, color, 3, 0.18);
  // Cytoplasm — wobbly outer membrane
  wobblyBlob(g, r * 1.0 * breathe, 14, 0.10, t * 1.6 + phase, phase);
  g.fill({ color, alpha: 0.85 });
  // Inner nucleus
  g.circle(0, 0, r * 0.45).fill({ color: mix(color, 0xffffff, 0.45), alpha: 0.9 });
  // Highlight on the nucleus
  g.circle(-r * 0.15, -r * 0.18, r * 0.15).fill({ color: 0xffffff, alpha: 0.55 });
}

/** Copepod — the tier-1 "plankton": teardrop body, two long forked
 *  antennae swept back, a single eye spot, a forked tail. Rows its
 *  antennae as it swims. */
export function drawCopepod(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading), sin = Math.sin(heading);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  const row = Math.sin(t * 9 + phase);
  // Antennae: long, swept back, rowing.
  for (const s of [-1, 1]) {
    const b = P(r * 0.7, s * r * 0.15);
    const tip = P(r * 0.2 - row * r * 0.25, s * r * (1.25 + row * 0.15));
    g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.3), width: 1.1, alpha: 0.8 });
  }
  // Tail fork + caudal setae.
  const tb = P(-r * 0.75, 0);
  for (const s of [-1, 1]) {
    const tip = P(-r * 1.35, s * r * 0.28 + row * s * r * 0.06);
    g.moveTo(tb.x, tb.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.3), width: 1.0, alpha: 0.8 });
  }
  // Body: teardrop (wide head, narrow abdomen) with segment lines.
  const pts: number[] = [];
  const N = 14;
  for (let i = 0; i <= N; i++) { const u = i / N; const a = r * 0.85 - u * r * 1.6; const hw = r * 0.5 * Math.sqrt(Math.max(0, 1 - u * u)) * (1 - u * 0.35) + r * 0.05; const q = P(a, -hw); pts.push(q.x, q.y); }
  for (let i = N; i >= 0; i--) { const u = i / N; const a = r * 0.85 - u * r * 1.6; const hw = r * 0.5 * Math.sqrt(Math.max(0, 1 - u * u)) * (1 - u * 0.35) + r * 0.05; const q = P(a, hw); pts.push(q.x, q.y); }
  g.poly(pts).fill({ color, alpha: 0.92 });
  g.poly(pts).stroke({ color: mix(color, 0x000000, 0.45), width: 1.0, alpha: 0.8 });
  for (let i = 1; i <= 3; i++) {
    const a = r * 0.2 - i * r * 0.28;
    const q1 = P(a, -r * 0.28), q2 = P(a, r * 0.28);
    g.moveTo(q1.x, q1.y).lineTo(q2.x, q2.y).stroke({ color: mix(color, 0x000000, 0.4), width: 0.7, alpha: 0.5 });
  }
  // Eye spot + a translucent gut.
  const e = P(r * 0.55, 0);
  g.circle(e.x, e.y, r * 0.13).fill({ color: 0x1a0608, alpha: 0.9 });
  const gut = P(r * 0.05, 0);
  g.circle(gut.x, gut.y, r * 0.16).fill({ color: mix(color, 0xffffff, 0.4), alpha: 0.5 });
}

/** Diatom — a glassy pill with striations, drifting with a slow spin
 *  and a faint inner glow. Barely steers; it's the easy meal. */
export function drawDiatom(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const h = heading + Math.sin(t * 0.6 + phase) * 0.35;
  const cos = Math.cos(h), sin = Math.sin(h);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  drawOriented(g, 0, 0, r * 1.1, r * 0.45, h, mix(color, 0xffffff, 0.25), 0.55);
  drawOriented(g, 0, 0, r * 1.1, r * 0.45, h, color, 0.6);
  drawOrientedStroke(g, 0, 0, r * 1.1, r * 0.45, h, mix(color, 0x000000, 0.35), 1.0, 0.8);
  // Striations across the frustule.
  for (let i = -4; i <= 4; i++) {
    const a = i * r * 0.22;
    const hw = r * 0.45 * Math.sqrt(Math.max(0, 1 - (a / (r * 1.1)) ** 2));
    const q1 = P(a, -hw), q2 = P(a, hw);
    g.moveTo(q1.x, q1.y).lineTo(q2.x, q2.y).stroke({ color: mix(color, 0x000000, 0.35), width: 0.6, alpha: 0.45 });
  }
  // Central raphe + glow.
  const q1 = P(-r * 0.95, 0), q2 = P(r * 0.95, 0);
  g.moveTo(q1.x, q1.y).lineTo(q2.x, q2.y).stroke({ color: 0xffffff, width: 0.8, alpha: 0.35 });
  g.circle(0, 0, r * 0.22).fill({ color: 0xffffff, alpha: 0.25 + 0.1 * Math.sin(t * 2 + phase) });
}

/** Ostracod — a seed shrimp: a bivalved bean-shaped shell with a hinge
 *  line, antennae poking out the front, quick and skittish. */
export function drawOstracod(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading), sin = Math.sin(heading);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  const kick = Math.sin(t * 11 + phase);
  // Antennae + swimming legs poking from the front gap.
  for (const s of [-1, 1]) {
    const b = P(r * 0.6, s * r * 0.12);
    const tip = P(r * 1.3, s * r * (0.45 + kick * 0.2));
    g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.35), width: 1.0, alpha: 0.85 });
    const lb = P(r * 0.3, s * r * 0.3);
    const lt = P(r * 0.1 - kick * r * 0.15, s * r * 0.85);
    g.moveTo(lb.x, lb.y).lineTo(lt.x, lt.y).stroke({ color: mix(color, 0x000000, 0.35), width: 0.8, alpha: 0.7 });
  }
  // Shell: bean shape, slightly flatter on the hinge (top) side.
  drawOriented(g, 0, 0, r * 0.95, r * 0.7, heading, 0x000000, 0.2);
  drawOriented(g, 0, 0, r * 0.9, r * 0.66, heading, color, 0.95);
  drawOrientedStroke(g, 0, 0, r * 0.9, r * 0.66, heading, mix(color, 0x000000, 0.45), 1.2, 0.85);
  // Hinge line + valve highlight.
  const h1 = P(-r * 0.7, -r * 0.3), h2 = P(r * 0.6, -r * 0.3);
  g.moveTo(h1.x, h1.y).lineTo(h2.x, h2.y).stroke({ color: mix(color, 0x000000, 0.45), width: 1.0, alpha: 0.7 });
  drawOriented(g, -cos * r * 0.15 + px * r * 0.15, -sin * r * 0.15 + py * r * 0.15, r * 0.45, r * 0.25, heading, 0xffffff, 0.18);
  // Eye at the front.
  const e = P(r * 0.55, -r * 0.12);
  g.circle(e.x, e.y, r * 0.11).fill({ color: 0x1a0608, alpha: 0.9 });
}

export function drawTrilobite(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  const perpX = -sin, perpY = cos;
  const wig = Math.sin(t * 4.5 + phase) * 0.10;

  // ----- Drop shadow under body -----
  drawOriented(g, 0, 0, r * 1.4, r * 0.95, heading, 0x000000, 0.25);

  // ----- Cephalon (head shield) -----
  // Wide rounded shield at the front (+heading side).
  const cephX = cos * r * 0.7;
  const cephY = sin * r * 0.7;
  drawOriented(g, cephX, cephY, r * 0.65, r * 0.85, heading, color, 0.95);
  drawOrientedStroke(g, cephX, cephY, r * 0.65, r * 0.85, heading, 0x2c1808, 1.5, 0.8);

  // Glabella — central raised ridge on the cephalon
  drawOriented(g, cephX, cephY, r * 0.35, r * 0.35, heading, mix(color, 0xffffff, 0.30), 0.9);
  // Two crescent eyes on either side of the glabella
  for (const side of [-1, 1]) {
    const eyeX = cephX + perpX * side * r * 0.45;
    const eyeY = cephY + perpY * side * r * 0.45;
    g.circle(eyeX, eyeY, r * 0.12).fill({ color: 0x1a0608, alpha: 0.95 });
    g.circle(eyeX, eyeY, r * 0.07).fill({ color: 0xff4020, alpha: 0.8 });
  }

  // ----- Thorax (segmented middle) -----
  // Five segments perpendicular to body axis, with the axial lobe down the center.
  const SEG = 5;
  for (let i = 0; i < SEG; i++) {
    const f = (i + 1) / (SEG + 2);
    const along = (0.5 - f) * r * 1.6; // from cephalon to pygidium
    const segWobble = wig * (i - SEG * 0.5);
    const sx = cos * along + perpX * segWobble * r * 0.1;
    const sy = sin * along + perpY * segWobble * r * 0.1;
    // Full segment width
    drawOriented(g, sx, sy, r * 0.20, r * 1.05, heading, color, 0.95);
    drawOrientedStroke(g, sx, sy, r * 0.20, r * 1.05, heading, 0x2c1808, 0.9, 0.65);
    // Axial lobe (raised center)
    drawOriented(g, sx, sy, r * 0.20, r * 0.35, heading, mix(color, 0xffffff, 0.18), 0.85);
  }

  // ----- Pygidium (tail shield) -----
  const pygX = -cos * r * 0.85;
  const pygY = -sin * r * 0.85;
  drawOriented(g, pygX, pygY, r * 0.45, r * 0.75, heading, color, 0.95);
  drawOrientedStroke(g, pygX, pygY, r * 0.45, r * 0.75, heading, 0x2c1808, 1.3, 0.8);
}

/** Opabinia — five stalked eyes, a long frontal proboscis with a claw,
 *  a soft segmented body with lateral swimming lobes and a tail fan. */
export function drawOpabinia(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading), sin = Math.sin(heading);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  drawOriented(g, 0, 0, r * 1.3, r * 0.7, heading, 0x000000, 0.22);
  // Lateral lobes rippling down the body.
  for (let i = 0; i < 8; i++) {
    const f = i / 7;
    const a = (0.45 - f) * r * 2.0;
    const rip = Math.sin(t * 5 + phase + i * 0.7) * 0.25;
    for (const s of [-1, 1]) {
      const b = P(a, s * r * 0.4);
      const tip = P(a - r * 0.1, s * r * (0.85 + rip));
      g.moveTo(b.x + cos * r * 0.12, b.y + sin * r * 0.12).lineTo(tip.x, tip.y).lineTo(b.x - cos * r * 0.12, b.y - sin * r * 0.12)
        .fill({ color: mix(color, 0xffffff, 0.25), alpha: 0.75 });
    }
  }
  // Body: soft segmented oval.
  drawOriented(g, 0, 0, r * 1.05, r * 0.42, heading, color, 0.96);
  drawOrientedStroke(g, 0, 0, r * 1.05, r * 0.42, heading, 0x2c1808, 1.2, 0.8);
  for (let i = -3; i <= 3; i++) {
    const a = i * r * 0.26;
    const hw = r * 0.4 * Math.sqrt(Math.max(0, 1 - (a / (r * 1.05)) ** 2));
    const q1 = P(a, -hw), q2 = P(a, hw);
    g.moveTo(q1.x, q1.y).lineTo(q2.x, q2.y).stroke({ color: 0x2c1808, width: 0.8, alpha: 0.45 });
  }
  // Tail fan: three blades.
  for (const s of [-1, 0, 1]) {
    const b = P(-r * 1.0, 0), tip = P(-r * 1.45, s * r * 0.35);
    g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.2), width: 2.2, alpha: 0.85 });
  }
  // Head with five stalked eyes.
  const h = P(r * 0.95, 0);
  g.circle(h.x, h.y, r * 0.32).fill({ color: mix(color, 0xffffff, 0.15), alpha: 0.98 });
  g.circle(h.x, h.y, r * 0.32).stroke({ color: 0x2c1808, width: 1.2, alpha: 0.85 });
  for (let i = 0; i < 5; i++) {
    const s = (i - 2) * 0.28;
    const e = P(r * 1.05 + Math.abs(s) * r * 0.1, s * r * 1.1);
    g.moveTo(h.x, h.y).lineTo(e.x, e.y).stroke({ color: 0x2c1808, width: 1.2, alpha: 0.8 });
    g.circle(e.x, e.y, r * 0.11).fill({ color: 0x1a0608, alpha: 0.95 });
    g.circle(e.x, e.y, r * 0.06).fill({ color: 0xff8040, alpha: 0.9 });
  }
  // Proboscis: a flexible trunk ending in a claw, sweeping side to side.
  const sway = Math.sin(t * 2.2 + phase) * 0.35;
  const pts: number[] = [];
  for (let i = 0; i <= 8; i++) {
    const f = i / 8;
    const q = P(r * 1.15 + f * r * 0.95, Math.sin(f * Math.PI) * sway * r * 0.5 + f * sway * r * 0.4);
    pts.push(q.x, q.y);
  }
  g.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]);
  g.stroke({ color: mix(color, 0x000000, 0.15), width: r * 0.16, alpha: 0.95 });
  const cx = pts[pts.length - 2], cy = pts[pts.length - 1];
  for (const s of [-1, 1]) {
    g.moveTo(cx, cy).lineTo(cx + cos * r * 0.18 + px * s * r * 0.16, cy + sin * r * 0.18 + py * s * r * 0.16)
      .stroke({ color: 0x2c1808, width: 1.8, alpha: 0.95 });
  }
}

/** Marrella — small delicate arthropod: head shield with two pairs of
 *  long backward-sweeping spines, a tapering segmented body, many legs. */
export function drawMarrella(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading), sin = Math.sin(heading);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  const legBeat = Math.sin(t * 9 + phase);
  // Legs: fine paired strokes, beating.
  for (let i = 0; i < 9; i++) {
    const a = r * 0.55 - i * r * 0.17;
    for (const s of [-1, 1]) {
      const b = P(a, s * r * 0.18);
      const tip = P(a - r * 0.12 + legBeat * r * 0.06 * ((i % 2) ? 1 : -1), s * r * 0.62);
      g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.3), width: 0.9, alpha: 0.7 });
    }
  }
  // Body: tapering segments.
  for (let i = 0; i < 9; i++) {
    const a = r * 0.55 - i * r * 0.17;
    const hw = r * 0.26 * (1 - i / 10);
    drawOriented(g, cos * a, sin * a, r * 0.09, hw, heading, color, 0.95);
  }
  // Head shield.
  const h = P(r * 0.75, 0);
  drawOriented(g, h.x, h.y, r * 0.32, r * 0.42, heading, mix(color, 0xffffff, 0.12), 0.97);
  drawOrientedStroke(g, h.x, h.y, r * 0.32, r * 0.42, heading, 0x2c1808, 1.1, 0.85);
  // Two pairs of long spines sweeping back from the shield.
  for (const s of [-1, 1]) {
    for (const [len, spread] of [[1.6, 0.55], [1.15, 0.95]] as Array<[number, number]>) {
      const b = P(r * 0.7, s * r * 0.3);
      const tip = P(r * 0.7 - len * r, s * r * spread);
      g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.25), width: 1.6, alpha: 0.9 });
    }
  }
  // Eyes.
  for (const s of [-1, 1]) {
    const e = P(r * 0.9, s * r * 0.18);
    g.circle(e.x, e.y, r * 0.08).fill({ color: 0x1a0608, alpha: 0.95 });
  }
  // Antennae.
  for (const s of [-1, 1]) {
    const b = P(r * 1.05, s * r * 0.1);
    const tip = P(r * 1.7, s * r * (0.35 + Math.sin(t * 3 + phase) * 0.1));
    g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.3), width: 0.9, alpha: 0.8 });
  }
}

/** Wiwaxia — a low, armoured slug: scale-covered oval with two rows of
 *  tall spines along the back. Slow, bristling. */
export function drawWiwaxia(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading), sin = Math.sin(heading);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  drawOriented(g, 0, 0, r * 1.1, r * 0.85, heading, 0x000000, 0.25);
  // Spines: two dorsal rows, slightly splayed, swaying.
  const sway = Math.sin(t * 1.5 + phase) * 0.08;
  for (let i = 0; i < 6; i++) {
    const a = (0.4 - i / 5 * 0.9) * r;
    for (const s of [-1, 1]) {
      const b = P(a, s * r * 0.28);
      const tip = P(a - r * 0.35, s * r * (1.15 + sway * s));
      g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0x000000, 0.35), width: 2.4, alpha: 0.95 });
      g.moveTo(b.x, b.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0xffffff, 0.35), width: 0.9, alpha: 0.8 });
    }
  }
  // Body oval with scale rows.
  drawOriented(g, 0, 0, r * 0.95, r * 0.72, heading, color, 0.97);
  drawOrientedStroke(g, 0, 0, r * 0.95, r * 0.72, heading, 0x2c1808, 1.4, 0.85);
  for (let row = -2; row <= 2; row++) {
    for (let i = -3; i <= 3; i++) {
      const a = i * r * 0.24 + (row % 2 ? r * 0.12 : 0);
      const s = row * r * 0.24;
      if ((a / (r * 0.95)) ** 2 + (s / (r * 0.72)) ** 2 > 0.8) continue;
      const q = P(a, s);
      g.circle(q.x, q.y, r * 0.09).stroke({ color: 0x2c1808, width: 0.7, alpha: 0.45 });
    }
  }
  // Front: a blunt head with a dark mouth slit.
  const m = P(r * 0.8, 0);
  g.circle(m.x, m.y, r * 0.09).fill({ color: 0x1a0608, alpha: 0.85 });
}

/** Hallucigenia — a thin worm walking on seven pairs of stilt legs, with
 *  matching pairs of long spines on its back and a tiny head. */
export function drawHallucigenia(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading), sin = Math.sin(heading);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  const N = 7;
  for (let i = 0; i < N; i++) {
    const a = (0.5 - i / (N - 1)) * r * 2.2;
    const step = Math.sin(t * 8 + phase + i * 1.1) * 0.25;
    // Stilt legs (both sides, seen from above they splay outward).
    for (const s of [-1, 1]) {
      const b = P(a, s * r * 0.12);
      const knee = P(a + step * r * 0.3, s * r * 0.55);
      const tip = P(a + step * r * 0.5, s * r * 0.95);
      g.moveTo(b.x, b.y).lineTo(knee.x, knee.y).lineTo(tip.x, tip.y)
        .stroke({ color: mix(color, 0x000000, 0.35), width: 1.3, alpha: 0.85 });
    }
    // Dorsal spines: a pair, drawn as a sharp V above the body midline.
    const sb = P(a + r * 0.08, 0);
    for (const s of [-1, 1]) {
      const tip = P(a + r * 0.3, s * r * 0.5);
      g.moveTo(sb.x, sb.y).lineTo(tip.x, tip.y).stroke({ color: mix(color, 0xffffff, 0.3), width: 2.0, alpha: 0.95 });
    }
  }
  // Body tube.
  const pts: number[] = [];
  for (let i = 0; i <= 12; i++) {
    const f = i / 12;
    const a = (0.6 - f) * r * 2.4;
    const q = P(a, Math.sin(t * 3 + phase + f * 6) * r * 0.06);
    pts.push(q.x, q.y);
  }
  g.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]);
  g.stroke({ color: 0x2c1808, width: r * 0.3, alpha: 0.9 });
  g.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]);
  g.stroke({ color, width: r * 0.22, alpha: 0.97 });
  // Tiny head with an eye and a mouth.
  const h = P(r * 1.4, 0);
  g.circle(h.x, h.y, r * 0.18).fill({ color: mix(color, 0xffffff, 0.15), alpha: 0.98 });
  g.circle(h.x, h.y, r * 0.18).stroke({ color: 0x2c1808, width: 1.1, alpha: 0.85 });
  g.circle(h.x + cos * r * 0.05, h.y + sin * r * 0.05, r * 0.07).fill({ color: 0xff5520, alpha: 0.9 });
}

/**
 * Threat cue drawn over a creature the player can't bite yet: a soft
 * red-orange ring that breathes, brighter while it hunts you, and a
 * sharp flash while it winds up a strike (`windup` = 1 → 0 as it lands).
 */
export function drawThreatRing(g: Graphics, r: number, t: number, hunting: boolean, windup: number): void {
  const pulse = 0.5 + 0.5 * Math.sin(t * (hunting ? 6 : 2.2));
  const base = hunting ? 0.55 : 0.22;
  const ring = r * 1.55 + pulse * r * 0.15;
  g.circle(0, 0, ring).stroke({ color: hunting ? 0xff4030 : 0xff8050, width: hunting ? 2.2 : 1.4, alpha: base + pulse * 0.25 });
  g.circle(0, 0, ring * 1.15).fill({ color: 0xff3020, alpha: (hunting ? 0.10 : 0.04) + pulse * 0.03 });
  if (windup > 0) {
    // Tell: a ring that contracts onto the body as the strike comes.
    const k = 1 - windup;
    g.circle(0, 0, r * (2.6 - k * 1.1)).stroke({ color: 0xfff0a0, width: 3, alpha: 0.35 + k * 0.6 });
  }
}

export function drawHuntingWorm(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  const perpX = -sin, perpY = cos;

  // Multi-segment articulated body with sinusoidal slither + parapodia (lateral legs).
  const SEG = 9;
  const segLen = r * 0.42;
  const segs: Array<{ x: number; y: number; w: number; bend: number }> = [];

  for (let i = 0; i < SEG; i++) {
    const f = i / (SEG - 1);
    const along = -i * segLen;
    const bend = Math.sin(t * 7 + phase + i * 0.85) * r * 0.42 * (0.2 + f * 0.8);
    const sx = cos * along + perpX * bend;
    const sy = sin * along + perpY * bend;
    // Width tapers from head to tail
    const sw = r * 0.50 * (1 - f * 0.55);
    segs.push({ x: sx, y: sy, w: sw, bend });
  }

  // Body shadow (slight offset perpendicular to suggest light direction)
  for (let i = 1; i < SEG; i++) {
    const a = segs[i - 1];
    const b = segs[i];
    g.moveTo(a.x + perpX * a.w + perpX * 1, a.y + perpY * a.w + perpY * 1)
      .lineTo(b.x + perpX * b.w + perpX * 1, b.y + perpY * b.w + perpY * 1)
      .lineTo(b.x - perpX * b.w + perpX * 1, b.y - perpY * b.w + perpY * 1)
      .lineTo(a.x - perpX * a.w + perpX * 1, a.y - perpY * a.w + perpY * 1)
      .fill({ color: 0x000000, alpha: 0.25 });
  }

  // Parapodia (lateral leg-fans) — small triangular fins on each segment, both sides
  for (let i = 1; i < SEG - 1; i++) {
    const s = segs[i];
    const flap = Math.sin(t * 12 + i * 0.5 + phase) * 0.3;
    for (const side of [-1, 1]) {
      const baseX = s.x + perpX * side * s.w * 0.8;
      const baseY = s.y + perpY * side * s.w * 0.8;
      const tipX = s.x + perpX * side * (s.w + r * 0.25 * (1 + flap));
      const tipY = s.y + perpY * side * (s.w + r * 0.25 * (1 + flap));
      g.moveTo(baseX - cos * s.w * 0.4, baseY - sin * s.w * 0.4)
        .lineTo(tipX, tipY)
        .lineTo(baseX + cos * s.w * 0.4, baseY + sin * s.w * 0.4)
        .fill({ color: mix(color, 0x000000, 0.2), alpha: 0.7 });
    }
  }

  // Body segments — flesh chain
  for (let i = 1; i < SEG; i++) {
    const a = segs[i - 1];
    const b = segs[i];
    g.moveTo(a.x + perpX * a.w, a.y + perpY * a.w)
      .lineTo(b.x + perpX * b.w, b.y + perpY * b.w)
      .lineTo(b.x - perpX * b.w, b.y - perpY * b.w)
      .lineTo(a.x - perpX * a.w, a.y - perpY * a.w)
      .fill({ color, alpha: 0.95 });
    // Segment divider line
    g.moveTo(b.x + perpX * b.w * 0.9, b.y + perpY * b.w * 0.9)
      .lineTo(b.x - perpX * b.w * 0.9, b.y - perpY * b.w * 0.9)
      .stroke({ color: 0x1a0408, width: 0.8, alpha: 0.55 });
  }

  // Head capsule — slightly larger, with mandibles and a glowing eye
  const head = segs[0];
  g.circle(head.x, head.y, r * 0.55).fill({ color, alpha: 0.98 });
  g.circle(head.x, head.y, r * 0.55).stroke({ color: 0x1a0408, width: 1.5, alpha: 0.85 });

  // Mandibles — two curved prongs at the front
  const mouthX = head.x + cos * r * 0.45;
  const mouthY = head.y + sin * r * 0.45;
  for (const side of [-1, 1]) {
    g.moveTo(mouthX, mouthY)
      .lineTo(mouthX + cos * r * 0.25 + perpX * side * r * 0.15,
              mouthY + sin * r * 0.25 + perpY * side * r * 0.15)
      .stroke({ color: 0x1a0408, width: 2.2, alpha: 0.95 });
  }

  // Glowing predator eye
  const eyePulse = 0.65 + 0.35 * Math.sin(t * 4.5 + phase);
  const eyeX = head.x + cos * r * 0.15 + perpX * r * 0.15;
  const eyeY = head.y + sin * r * 0.15 + perpY * r * 0.15;
  g.circle(eyeX, eyeY, r * 0.18).fill({ color: 0xff5520, alpha: 0.85 + 0.15 * eyePulse });
  g.circle(eyeX + cos * r * 0.04, eyeY + sin * r * 0.04, r * 0.07).fill({ color: 0xfff0a0 });
}

export function drawAnomalocaris(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  const perpX = -sin, perpY = cos;

  // Drop shadow under body
  drawOriented(g, 0, 0, r * 2.0, r * 1.1, heading, 0x000000, 0.30);

  // ----- Lateral swimming lobes (the iconic ripple fringe) -----
  // 11 pairs of lobes along the body, rippling in a phase-offset wave so they
  // look like an undulating swimming motion.
  const LOBE = 11;
  for (let i = 0; i < LOBE; i++) {
    const f = i / (LOBE - 1);
    const along = (0.5 - f) * r * 2.5; // from head (+along) to tail (-along)
    const ripple = Math.sin(t * 4 + phase + i * 0.55) * 0.7;
    for (const side of [-1, 1]) {
      const baseX = cos * along + perpX * side * r * 0.55;
      const baseY = sin * along + perpY * side * r * 0.55;
      const tipX = cos * along + perpX * side * r * (1.05 + ripple * 0.25);
      const tipY = sin * along + perpY * side * r * (1.05 + ripple * 0.25);
      // Lobe shape: tapered triangle
      const w = r * 0.10 * (0.5 + f * 0.5 + Math.abs(ripple) * 0.3);
      g.moveTo(baseX + cos * w, baseY + sin * w)
        .lineTo(tipX, tipY)
        .lineTo(baseX - cos * w, baseY - sin * w)
        .fill({ color: mix(color, 0x000000, 0.10), alpha: 0.85 });
      g.moveTo(baseX + cos * w, baseY + sin * w)
        .lineTo(tipX, tipY)
        .lineTo(baseX - cos * w, baseY - sin * w)
        .stroke({ color: 0x080010, width: 0.8, alpha: 0.7 });
    }
  }

  // ----- Main body -----
  // Elongated oval, segmented down the back.
  drawOriented(g, 0, 0, r * 1.5, r * 1.0, heading, color, 0.98);
  drawOrientedStroke(g, 0, 0, r * 1.5, r * 1.0, heading, 0x080010, 2.5, 0.95);

  // Body segments — perpendicular fade-lines down the back
  for (let i = -2; i <= 2; i++) {
    const along = i * r * 0.45;
    const sx = cos * along;
    const sy = sin * along;
    g.moveTo(sx + perpX * r * 0.85, sy + perpY * r * 0.85)
      .lineTo(sx - perpX * r * 0.85, sy - perpY * r * 0.85)
      .stroke({ color: 0x080010, width: 1.0, alpha: 0.45 });
  }

  // ----- Head + stalked compound eyes -----
  const headX = cos * r * 1.2;
  const headY = sin * r * 1.2;
  // Head cap — slightly darker so it reads as a distinct module
  drawOriented(g, headX, headY, r * 0.55, r * 0.85, heading, mix(color, 0x301010, 0.4), 0.95);
  drawOrientedStroke(g, headX, headY, r * 0.55, r * 0.85, heading, 0x080010, 1.5, 0.95);

  // Stalked compound eyes — twin red orbs raised on stalks
  for (const side of [-1, 1]) {
    const stalkBaseX = headX + perpX * side * r * 0.35;
    const stalkBaseY = headY + perpY * side * r * 0.35;
    const stalkTipX = headX + perpX * side * r * 0.7 + cos * r * 0.1;
    const stalkTipY = headY + perpY * side * r * 0.7 + sin * r * 0.1;
    g.moveTo(stalkBaseX, stalkBaseY).lineTo(stalkTipX, stalkTipY)
      .stroke({ color: 0x080010, width: 2.5, alpha: 0.9 });
    g.circle(stalkTipX, stalkTipY, r * 0.20).fill({ color: 0xff0020, alpha: 0.95 });
    g.circle(stalkTipX, stalkTipY, r * 0.20).stroke({ color: 0x080010, width: 1.2, alpha: 0.9 });
    g.circle(stalkTipX + cos * r * 0.05, stalkTipY + sin * r * 0.05, r * 0.07).fill({ color: 0xffe0c0 });
  }

  // ----- Frontal raptorial appendages (the great appendages — diagnostic!) -----
  // Two large curving appendages projecting from beneath the head, each with
  // an inner row of sharp spines. These are what makes Anomalocaris iconic.
  for (const side of [-1, 1]) {
    const baseX = headX + perpX * side * r * 0.20;
    const baseY = headY + perpY * side * r * 0.20;
    const grasp = Math.sin(t * 1.8 + phase) * 0.25;
    // Curve the appendage outward then inward — three control segments
    const m1X = baseX + cos * r * 0.5 + perpX * side * r * 0.5;
    const m1Y = baseY + sin * r * 0.5 + perpY * side * r * 0.5;
    const tipX = baseX + cos * r * 0.9 + perpX * side * r * (0.2 - grasp);
    const tipY = baseY + sin * r * 0.9 + perpY * side * r * (0.2 - grasp);
    // Appendage body (thick chain)
    const segments = [
      { x: baseX, y: baseY, w: r * 0.20 },
      { x: m1X, y: m1Y, w: r * 0.16 },
      { x: tipX, y: tipY, w: r * 0.10 },
    ];
    for (let i = 1; i < segments.length; i++) {
      const a = segments[i - 1];
      const b = segments[i];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx2 = -dy / len, ny2 = dx / len;
      g.moveTo(a.x + nx2 * a.w, a.y + ny2 * a.w)
        .lineTo(b.x + nx2 * b.w, b.y + ny2 * b.w)
        .lineTo(b.x - nx2 * b.w, b.y - ny2 * b.w)
        .lineTo(a.x - nx2 * a.w, a.y - ny2 * a.w)
        .fill({ color: mix(color, 0x300018, 0.5), alpha: 0.95 });
      g.moveTo(a.x + nx2 * a.w, a.y + ny2 * a.w)
        .lineTo(b.x + nx2 * b.w, b.y + ny2 * b.w)
        .lineTo(b.x - nx2 * b.w, b.y - ny2 * b.w)
        .lineTo(a.x - nx2 * a.w, a.y - ny2 * a.w)
        .stroke({ color: 0x080010, width: 1.2, alpha: 0.85 });
    }
    // Spines — small inward-pointing barbs on the inner edge
    for (let i = 0; i < 5; i++) {
      const f = (i + 0.5) / 5;
      const px = baseX + (tipX - baseX) * f;
      const py = baseY + (tipY - baseY) * f;
      // Inward normal (back toward body)
      const inX = perpX * -side;
      const inY = perpY * -side;
      g.moveTo(px, py)
        .lineTo(px + inX * r * 0.18, py + inY * r * 0.18)
        .stroke({ color: 0xff4040, width: 1.8, alpha: 0.9 });
    }
  }

  // ----- Tail fan -----
  // Three pointed lobes spreading from the rear, like a paddle.
  const tailBaseX = -cos * r * 1.5;
  const tailBaseY = -sin * r * 1.5;
  for (const side of [-1, 0, 1]) {
    const offX = perpX * side * r * 0.4 - cos * r * 0.3;
    const offY = perpY * side * r * 0.4 - sin * r * 0.3;
    const tipX = tailBaseX + offX;
    const tipY = tailBaseY + offY;
    g.moveTo(tailBaseX, tailBaseY)
      .lineTo(tipX + perpX * 0.08 * r, tipY + perpY * 0.08 * r)
      .lineTo(tipX - perpX * 0.08 * r, tipY - perpY * 0.08 * r)
      .lineTo(tailBaseX, tailBaseY)
      .fill({ color: mix(color, 0x000000, 0.15), alpha: 0.85 });
    g.moveTo(tailBaseX, tailBaseY)
      .lineTo(tipX + perpX * 0.08 * r, tipY + perpY * 0.08 * r)
      .lineTo(tipX - perpX * 0.08 * r, tipY - perpY * 0.08 * r)
      .stroke({ color: 0x080010, width: 1, alpha: 0.85 });
  }

  // Dorsal pulse halo
  const pulse = 0.5 + 0.5 * Math.sin(t * 1.6 + phase);
  drawOrientedStroke(g, 0, 0, r * 1.65, r * 1.15, heading, 0xff3060, 2, 0.30 + 0.25 * pulse);
}

// ---- internal: oriented ellipse helpers ----------------------------

/**
 * Draw an ellipse "rotated" along a heading. Real ellipse() doesn't take a
 * rotation, so we approximate by drawing a polygon. Cheap (16 verts) and
 * indistinguishable from a true ellipse at our entity sizes.
 */
function drawOriented(g: Graphics, x: number, y: number, halfA: number, halfB: number, heading: number, color: number, alpha: number): void {
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  const N = 18;
  const pts: number[] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    const lx = Math.cos(a) * halfA;
    const ly = Math.sin(a) * halfB;
    pts.push(x + lx * cos - ly * sin, y + lx * sin + ly * cos);
  }
  g.poly(pts).fill({ color, alpha });
}

function drawOrientedStroke(g: Graphics, x: number, y: number, halfA: number, halfB: number, heading: number, color: number, width: number, alpha: number): void {
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  const N = 18;
  const pts: number[] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    const lx = Math.cos(a) * halfA;
    const ly = Math.sin(a) * halfB;
    pts.push(x + lx * cos - ly * sin, y + lx * sin + ly * cos);
  }
  g.poly(pts).stroke({ color, width, alpha });
}

/** Glow medusa — a small jelly with a soft green-gold bioluminescent core
 *  and trailing tentacles. The pulsing halo makes it easy to spot: eating
 *  one heals. */
export function drawGlowMedusa(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const beat = Math.sin(t * 3 + phase);
  const cos = Math.cos(heading), sin = Math.sin(heading);
  const px = -sin, py = cos;
  const P = (a: number, s: number) => ({ x: cos * a + px * s, y: sin * a + py * s });
  // Halo.
  g.circle(0, 0, r * (2.0 + beat * 0.2)).fill({ color, alpha: 0.10 + beat * 0.04 });
  // Tentacles trail behind the bell.
  for (let i = -2; i <= 2; i++) {
    const b = P(-r * 0.3, i * r * 0.22);
    const m = P(-r * 1.1, i * r * 0.28 + Math.sin(t * 4 + phase + i) * r * 0.25);
    const e = P(-r * 1.9, i * r * 0.3 + Math.sin(t * 4 + phase + i + 1.5) * r * 0.35);
    g.moveTo(b.x, b.y).quadraticCurveTo(m.x, m.y, e.x, e.y).stroke({ color: mix(color, 0xffffff, 0.4), width: 0.8, alpha: 0.55 });
  }
  // Bell — contracts on the beat.
  const bw = r * (0.95 - beat * 0.1);
  drawOriented(g, r * 0.1, 0, r * 0.75, bw, heading, mix(color, 0xffffff, 0.3), 0.45);
  drawOrientedStroke(g, r * 0.1, 0, r * 0.75, bw, heading, mix(color, 0xffffff, 0.6), 1.0, 0.8);
  // Glowing core.
  const c = P(r * 0.15, 0);
  g.circle(c.x, c.y, r * 0.4).fill({ color: 0xfff6a0, alpha: 0.7 + beat * 0.2 });
  g.circle(c.x, c.y, r * 0.18).fill({ color: 0xffffff, alpha: 0.9 });
}

/** Toxic siphonophore — a violet float bristling with stinging spines and
 *  yellow warning spots. Small enough to eat, and you shouldn't. */
export function drawToxicSiphon(g: Graphics, ctx: CreatureDrawCtx): void {
  g.clear();
  const { r, t, phase, heading, color } = ctx;
  const throb = Math.sin(t * 5 + phase);
  // Sickly warning ring.
  g.circle(0, 0, r * 1.75).stroke({ color: 0xc8ff40, width: 1.2, alpha: 0.35 + throb * 0.15 });
  // Stinging spines all round.
  const n = 10;
  for (let i = 0; i < n; i++) {
    const a = heading + (i / n) * Math.PI * 2 + Math.sin(t * 2 + phase) * 0.1;
    const len = r * (1.45 + 0.15 * Math.sin(t * 6 + i + phase));
    g.moveTo(Math.cos(a) * r * 0.7, Math.sin(a) * r * 0.7)
      .lineTo(Math.cos(a) * len, Math.sin(a) * len)
      .stroke({ color: mix(color, 0xffffff, 0.2), width: 1.3, alpha: 0.9 });
    g.circle(Math.cos(a) * len, Math.sin(a) * len, 1.1).fill({ color: 0xc8ff40, alpha: 0.9 });
  }
  // Body.
  drawOriented(g, 0, 0, r * 0.95, r * 0.8, heading, color, 0.95);
  drawOrientedStroke(g, 0, 0, r * 0.95, r * 0.8, heading, mix(color, 0x000000, 0.45), 1.2, 0.9);
  // Warning spots.
  const cos = Math.cos(heading), sin = Math.sin(heading);
  for (const [a, s] of [[0.35, 0.3], [-0.3, -0.35], [-0.25, 0.4], [0.3, -0.3]]) {
    const x = cos * a * r - sin * s * r, y = sin * a * r + cos * s * r;
    g.circle(x, y, r * 0.17).fill({ color: 0xffe030, alpha: 0.95 });
  }
}
